import React, { useCallback, useEffect } from 'react'
import { StyleSheet } from 'react-native'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { makeLoginFactory } from '@/main/factories/pages'
import { makeDateAdapterFactory } from '@/main/adapters'
import { makeMetricsLogoff } from '@/main/factories/usecases'
import { GetRedesSociais, GetTheme, PortalLogout, GetProspectConfigs, UpdatePassword, MetricsLogoff } from '@/domain/usecases'
import { DateAdapter } from '@/data/protocols'
import { JwtAdapter } from '@/infra/adapters'
import { useAuth, useNotify, useProspectSettings, useTheme, useThemeClient } from '@/presentation/hooks'
import { Button, InstallationSuggestion, Logout, NavigationDrawer, PhoneModal, SnackBarProvider, Snackbar, UpdatePasswordC, authenticationState, errorAuthState, phoneModalNumberState, socialMediaState } from '@/presentation/components'
import { version } from '../../../../package.json'

type RouterProps = {
  getTheme: GetTheme
  getRedesSociais: GetRedesSociais
  getProspectConfigs: GetProspectConfigs
  portalLogout: PortalLogout
  updatePassword: UpdatePassword
  dateAdapter: DateAdapter
  jwtAdapter: JwtAdapter
}

const EMMITERS_AUTHORIZED = [
  'http://integraservice.insidesistemas.com.br',
  'https://login.insidesistemas.com.br/api/v1/'
]

const ROLES_AUTHORIZED = [
  'Portal',
  'Service'
]

const Router: React.FC<RouterProps> = ({
  portalLogout,
  getTheme,
  getRedesSociais,
  getProspectConfigs,
  updatePassword,
  dateAdapter,
  jwtAdapter
}) => {
  const theme = useTheme()
  const notify = useNotify()
  const { logout, getCurrentClient } = useAuth()
  const { setThemeClient } = useThemeClient()
  const { startConfig } = useProspectSettings()
  const { accessToken } = useRecoilValue(authenticationState)
  const [error, setError] = useRecoilState(errorAuthState)
  const setSocialMedia = useSetRecoilState(socialMediaState)
  const setPhoneModal = useSetRecoilState(phoneModalNumberState)

  const codCliente = getCurrentClient()

  useEffect(() => {
    getTheme
      .get()
      .then(setThemeClient)
      .catch(console.error)

    getRedesSociais
      .getRedesSociais()
      .then((socialMedias) => {
        setSocialMedia(socialMedias)
        setPhoneModal(socialMedias.phone)
      }
      )
      .catch(console.error)

    getProspectConfigs
      .getAll()
      .then(startConfig)
      .catch(console.error)
  }, [])

  const isValidLogin = useCallback((): boolean => {
    const today = dateAdapter.today()
    const expireDate = new Date(0)

    if (!accessToken?.access_token) {
      return false
    }

    const auth = jwtAdapter.decode(accessToken?.access_token)
    if (!auth || !EMMITERS_AUTHORIZED.includes(auth.iss)) {
      notify.error('Token inválido, faça o login novamente!')
      logout()
      return false
    }

    const roles = auth['integraservice-webapi'] || auth['portal-service']
    if (typeof roles === 'string') {
      if (!ROLES_AUTHORIZED.includes(roles)) {
        notify.error('Token inválido, faça o login novamente!')
        logout()
        return false
      }
    } else {
      if ((roles as []).every(role => !ROLES_AUTHORIZED.includes(role))) {
        notify.error('Token inválido, faça o login novamente!')
        logout()
        return false
      }
    }

    expireDate.setUTCSeconds(auth.exp)
    const isExpired = today > expireDate

    if (isExpired) {
      notify.error('Sessão Expirada, faça o login novamente!')
      logout()
      if (codCliente) {
        void portalLogout
          .logout({ cliente: parseInt(codCliente), version })
          .catch(console.error)
      }

      return false
    }

    return true
  }, [accessToken])

  const styles = StyleSheet.create({
    container: {
      position: 'absolute',
      width: '100%',
      height: 'fit-content',
      zIndex: 5,
      paddingTop: 12,
      paddingHorizontal: 24,
      top: 60
    }
  })

  const containerStyles = {
    info: {},
    success: {
      backgroundColor: theme.colors.success,
      color: theme.colors.white
    },
    error: {
      backgroundColor: theme.colors.error,
      color: theme.colors.white
    }
  }

  return (
    <>
      {!!error.length &&
        <Snackbar
          message={error}
          action={
            <Button
              variant="text"
              title="FECHAR"
              onPress={() => setError('')}
              compact
            />
          }
          style={{
            ...containerStyles,
            ...styles.container,
            display: error.length ? 'flex' : 'none'
          }}
        />
      }
      <SnackBarProvider />
      <Logout
        portalLogout={portalLogout}
        dateAdapter={makeDateAdapterFactory()}
        metricsLogoff={makeMetricsLogoff()}
      />
      <UpdatePasswordC updatePassword={updatePassword} />
      <InstallationSuggestion />
      {isValidLogin()
        ? <NavigationDrawer />
        : makeLoginFactory()
      }
      <PhoneModal/>
    </>
  )
}

export default Router
